import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";
import { ImageOutputSizeError, optimizeImageFile } from "../../../utils/imageUpload";
import { UserFacingError } from "../../../utils/userFacingError";

const BYTES_PER_MEGABYTE = 1024 * 1024;
const EMAIL_IMAGE_MAX_SOURCE_BYTES = 20 * BYTES_PER_MEGABYTE;
const EMAIL_IMAGE_MAX_OUTPUT_BYTES = 1.25 * BYTES_PER_MEGABYTE;
const EMAIL_IMAGE_MAX_DIMENSION = 1600;

const imageMimeTypeByExtension: Record<string, string> = {
  avif: "image/avif",
  bmp: "image/bmp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  svg: "image/svg+xml",
  webp: "image/webp",
};

const transparencyCapableImageTypes = new Set([
  "image/gif",
  "image/png",
  "image/svg+xml",
]);

export const emailImageAccept = "image/*";

const getFileExtension = (fileName: string): string =>
  fileName.split(".").pop()?.trim().toLowerCase() || "";

const getSourceImageMimeType = ({ file }: { file: File }): string => {
  const browserMimeType = file.type.trim().toLowerCase();

  if (browserMimeType.startsWith("image/")) {
    return browserMimeType;
  }

  return imageMimeTypeByExtension[getFileExtension(file.name)] || "";
};

export const isEmailImageFile = ({ file }: { file: File }): boolean =>
  !!getSourceImageMimeType({ file });

// The file picker allows "All files", so image tiles check the type before
// any upload starts (APBOT, Oct 2026: a list file reached the error page).
export const emailImageFileTypeError =
  "This file is not an image. Choose a PNG, JPG, GIF, or WebP image.";

export const getEmailImageFileError = ({ file }: { file: File }): string =>
  isEmailImageFile({ file }) ? "" : emailImageFileTypeError;

// Upload failures show their message next to the control that started the
// upload instead of sending staff to the error page. Unexpected failures are
// still reported.
export const emailAssetUploadApolloContext =
  getPreserveExpectedErrorsForCallerContext({
    expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
  });

const withSourceImageMimeType = ({
  file,
  sourceMimeType,
}: {
  file: File;
  sourceMimeType: string;
}): File =>
  file.type === sourceMimeType
    ? file
    : new File([file], file.name, {
        lastModified: file.lastModified,
        type: sourceMimeType,
      });

const getEmailSafeImageFileName = ({
  fileName,
  outputMimeType,
}: {
  fileName: string;
  outputMimeType: "image/jpeg" | "image/png";
}): string => {
  const baseName = fileName.replace(/\.[^.]+$/, "").trim() || "email-image";
  return `${baseName}.${outputMimeType === "image/png" ? "png" : "jpg"}`;
};

export const prepareEmailAssetForUpload = async ({
  file,
}: {
  file: File;
}): Promise<File> => {
  const sourceMimeType = getSourceImageMimeType({ file });

  // PDFs and other allowed email attachments use the same presigned uploader,
  // but only visual assets should be rasterized.
  if (!sourceMimeType) {
    return file;
  }

  if (file.size <= 0) {
    throw new UserFacingError("This image file is empty. Choose another image.");
  }

  if (file.size > EMAIL_IMAGE_MAX_SOURCE_BYTES) {
    throw new UserFacingError("This image is larger than 20 MB. Choose a smaller image.");
  }

  const outputMimeType: "image/jpeg" | "image/png" =
    transparencyCapableImageTypes.has(sourceMimeType)
      ? "image/png"
      : "image/jpeg";
  const sourceImageFile = withSourceImageMimeType({ file, sourceMimeType });

  try {
    const normalizedImage = await optimizeImageFile({
      file: sourceImageFile,
      outputMimeType,
      maxOutputBytes: EMAIL_IMAGE_MAX_OUTPUT_BYTES,
      maxDimension: EMAIL_IMAGE_MAX_DIMENSION,
    });

    return new File(
      [normalizedImage],
      getEmailSafeImageFileName({
        fileName: file.name,
        outputMimeType,
      }),
      {
        lastModified: file.lastModified,
        type: outputMimeType,
      }
    );
  } catch (error) {
    if (
      error instanceof ImageOutputSizeError ||
      (error instanceof Error &&
        error.message === "The optimized image is still too large for email.")
    ) {
      throw new UserFacingError(
        "This image is still too large for email after we made it smaller. Choose a smaller image or crop it, then try again."
      );
    }

    throw new UserFacingError(
      "This image could not be converted for email. Try exporting it as a PNG or JPG."
    );
  }
};
